/**
 * Adapter WebSocket — produção + staging.
 *
 * Implementa a mesma interface do mock adapter. Conecta em
 * /ws/module_lab/{session_id} via wss://<host>/ws/... (nginx proxya pra :5004).
 *
 * Recursos:
 * - Reconnect exponencial em onclose (start 1s, cap 30s) se não foi intencional
 * - Pending queue para mensagens enviadas enquanto readyState !== OPEN
 * - Emite 'sessionSnapshot' quando recebe handshake; adotamos o snapshot como
 *   fonte de verdade ao conectar/reconectar (descarta estado local)
 *
 * Protocolo em docs/module_lab/ADAPTER_CONTRACT.md §Protocolo.
 */

import type {
  Adapter,
  AssignmentEntry,
  InteractionMode,
  LabComponentStateSnapshot,
  OwnResponseEntry,
  PairedResponseEntry,
  PostitEntry,
  QuizStateSnapshot,
  RevealedResponse,
  ScoreSnapshot,
  Unsubscribe,
} from '../types/adapter'
import type { TelemetryEvent } from '../types/events'
import type { Role } from '../types/session'

interface InternalState {
  currentSlideIndex: number
  currentActivityId: string | null
  interactionMode: InteractionMode
  quizzes: Record<string, QuizStateSnapshot>
  scores: Record<string, number>
  membershipId: string | null
  pairedResponses: Record<string, PairedResponseEntry>
  /** key = slideIndex como string, valor = resposta do próprio aluno nesse slide */
  ownResponsesBySlide: Record<string, OwnResponseEntry>
  /** key = slideIndex como string, valor = respostas liberadas pelo mestre */
  revealedBySlide: Record<string, RevealedResponse[]>
  /** key = `${missionId}:${slideIndex}` */
  labComponentStates: Record<string, LabComponentStateSnapshot>
  /** key = slideIndex (number como string), valor = atribuicao de equipe do aluno */
  myAssignmentBySlide: Record<string, AssignmentEntry>
  /** key = slideIndex como string, valor = lista de postits do mural */
  postitsBySlide: Record<string, PostitEntry[]>
}

type Listener = (payload: any) => void

export interface WsAdapterParams {
  sessionId: string
  userId: string
  role: Role
  /** Base URL do WS. Default: deriva do location atual. */
  wsBaseUrl?: string
  /** JWT opcional. Se ausente, envia anonymous_user_id=<userId>. */
  token?: string | null
  /** Nome de exibição opcional. */
  displayName?: string
}

function resolveDefaultBase(): string {
  if (typeof window === 'undefined') return 'ws://localhost:5004'
  const { protocol, host } = window.location
  const wsProtocol = protocol === 'https:' ? 'wss:' : 'ws:'
  return `${wsProtocol}//${host}`
}

export function createWebSocketAdapter(params: WsAdapterParams): Adapter {
  const {
    sessionId,
    userId,
    role,
    wsBaseUrl = resolveDefaultBase(),
    token,
    displayName,
  } = params

  const state: InternalState = {
    currentSlideIndex: 0,
    currentActivityId: null,
    interactionMode: 'free',
    quizzes: {},
    scores: {},
    membershipId: null,
    pairedResponses: {},
    ownResponsesBySlide: {},
    revealedBySlide: {},
    labComponentStates: {},
    myAssignmentBySlide: {},
    postitsBySlide: {},
  }

  const listeners = new Map<string, Set<Listener>>()
  const pending: unknown[] = []

  // Cache do último snapshot pra replay quando um listener é registrado depois
  // do snapshot já ter chegado (race condition comum quando o adapter conecta
  // muito rápido — caso embed na GameSession).
  let lastSnapshot: Record<string, unknown> | null = null

  let ws: WebSocket | null = null
  let reconnectDelay = 1000 // ms
  const MAX_RECONNECT_DELAY = 30000
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let intentionallyClosed = false

  function emit(event: string, payload: any): void {
    listeners.get(event)?.forEach((cb) => {
      try {
        cb(payload)
      } catch (err) {
        console.error('[module_lab/ws] listener error', err)
      }
    })
  }

  function send(obj: Record<string, unknown>): void {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(obj))
    } else {
      pending.push(obj)
    }
  }

  function flushPending(): void {
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    while (pending.length) {
      ws.send(JSON.stringify(pending.shift()))
    }
  }

  function buildUrl(): string {
    const url = new URL(`${wsBaseUrl}/ws/module_lab/${encodeURIComponent(sessionId)}`)
    if (token) {
      url.searchParams.set('token', token)
    } else {
      url.searchParams.set('anonymous_user_id', userId)
    }
    if (displayName) url.searchParams.set('display_name', displayName)
    return url.toString()
  }

  function connect(): void {
    if (intentionallyClosed) return

    ws = new WebSocket(buildUrl())

    ws.onopen = () => {
      reconnectDelay = 1000
      send({ type: 'join', displayName })
      flushPending()
      emit('connected', undefined)
    }

    ws.onmessage = (ev: MessageEvent) => {
      let msg: Record<string, unknown>
      try {
        msg = JSON.parse(ev.data as string)
      } catch {
        return
      }
      handleIncoming(msg)
    }

    ws.onerror = (err: Event) => {
      console.warn('[module_lab/ws] erro', err)
      emit('error', err)
    }

    ws.onclose = () => {
      emit('disconnected', undefined)
      if (!intentionallyClosed) {
        scheduleReconnect()
      }
    }
  }

  function scheduleReconnect(): void {
    if (reconnectTimer) clearTimeout(reconnectTimer)
    reconnectTimer = setTimeout(() => {
      reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY)
      connect()
    }, reconnectDelay)
  }

  function handleIncoming(msg: Record<string, unknown>): void {
    const type = msg.type as string
    if (!type) return

    if (type === 'sessionSnapshot') {
      state.currentSlideIndex = (msg.currentSlideIndex as number) ?? 0
      state.currentActivityId = (msg.currentActivityId as string | null) ?? null
      state.interactionMode = (msg.interactionMode as InteractionMode) ?? 'free'
      state.quizzes = (msg.quizzes as Record<string, QuizStateSnapshot>) ?? {}
      state.membershipId = (msg.membershipId as string) ?? null
      state.scores = {}
      const scoresArr = (msg.scores as Array<{ membershipId: string; total: number }>) ?? []
      for (const s of scoresArr) state.scores[s.membershipId] = s.total
      state.pairedResponses = (msg.pairedResponses as Record<string, PairedResponseEntry>) ?? {}
      state.ownResponsesBySlide = (msg.ownResponsesBySlide as Record<string, OwnResponseEntry>) ?? {}
      state.revealedBySlide = (msg.revealedBySlide as Record<string, RevealedResponse[]>) ?? {}
      state.myAssignmentBySlide = (msg.myAssignmentBySlide as Record<string, AssignmentEntry>) ?? {}
      state.postitsBySlide = (msg.postitsBySlide as Record<string, PostitEntry[]>) ?? {}
      lastSnapshot = msg
      emit('sessionSnapshot', msg)
      // replay local pra listeners individuais
      emit('slideChange', { index: state.currentSlideIndex })
      if (state.currentActivityId !== null) {
        emit('activityChange', { activityId: state.currentActivityId })
      }
      emit('interactionModeChange', { mode: state.interactionMode })
      return
    }

    if (type === 'slideChange') {
      state.currentSlideIndex = msg.index as number
      emit('slideChange', { index: msg.index })
      return
    }
    if (type === 'activityChange') {
      state.currentActivityId = (msg.activityId as string) ?? null
      emit('activityChange', { activityId: state.currentActivityId })
      return
    }
    if (type === 'interactionModeChange') {
      state.interactionMode = (msg.mode as InteractionMode) ?? 'free'
      emit('interactionModeChange', { mode: state.interactionMode })
      return
    }
    if (type === 'quizState') {
      const qs = msg as unknown as QuizStateSnapshot
      state.quizzes[qs.questionId] = qs
      emit('quizState', qs)
      return
    }
    if (type === 'scoreUpdate') {
      const deltas = (msg.deltas as Array<{ membershipId: string; delta: number }>) ?? []
      for (const d of deltas) {
        state.scores[d.membershipId] = (state.scores[d.membershipId] ?? 0) + d.delta
      }
      emit('scoreUpdate', msg)
      return
    }
    if (type === 'participantUpdate') {
      emit('participantUpdate', msg)
      return
    }
    if (type === 'labComponentState') {
      const snap: LabComponentStateSnapshot = {
        missionId: msg.missionId as string,
        slideIndex: msg.slideIndex as number,
        aggregate: (msg.aggregate as Record<string, unknown>) ?? {},
        totalPlayers: (msg.totalPlayers as number) ?? 0,
      }
      const key = `${snap.missionId}:${snap.slideIndex}`
      state.labComponentStates[key] = snap
      emit('labComponentState', snap)
      return
    }
    if (type === 'assignmentReceived') {
      const slideIndex = msg.slideIndex as number
      const teamId = msg.teamId as string
      const payload = (msg.myAssignment as Record<string, unknown>) ?? {}
      state.myAssignmentBySlide[String(slideIndex)] = { teamId, payload }
      emit('assignmentReceived', { slideIndex, teamId, payload })
      return
    }
    if (type === 'postitChange') {
      const action = msg.action as string
      if (action === 'deleted') {
        const id = msg.postitId as string
        for (const list of Object.values(state.postitsBySlide)) {
          const idx = list.findIndex((p) => p.id === id)
          if (idx >= 0) list.splice(idx, 1)
        }
      } else {
        const p = msg.postit as PostitEntry | undefined
        if (p && typeof p.slideIndex === 'number') {
          const key = String(p.slideIndex)
          const list = state.postitsBySlide[key] ?? []
          const idx = list.findIndex((x) => x.id === p.id)
          if (idx >= 0) {
            list[idx] = p
          } else {
            list.push(p)
          }
          state.postitsBySlide[key] = list
        }
      }
      emit('postitChange', msg)
      return
    }
    if (type === 'openQuestionRevealed') {
      const key = String(msg.slideIndex as number)
      const membershipId = msg.membershipId as string
      const revealed = msg.revealed as boolean
      const list = state.revealedBySlide[key] ?? []
      const idx = list.findIndex((r) => r.membershipId === membershipId)
      if (revealed) {
        const entry: RevealedResponse = {
          membershipId,
          displayName: (msg.displayName as string) ?? 'Aluno',
          pairId: msg.pairId as string,
          value: msg.value as string,
        }
        if (idx >= 0) list[idx] = entry
        else list.push(entry)
      } else if (idx >= 0) {
        list.splice(idx, 1)
      }
      state.revealedBySlide[key] = list
      emit('openQuestionRevealed', msg)
      return
    }
    if (
      type === 'openQuestionAck' ||
      type === 'confidenceAck' ||
      type === 'quizJustifiedAck' ||
      type === 'formAck'
    ) {
      emit(type, msg)
      return
    }
    if (type === 'sessionEnded') {
      // Mestre encerrou a aula. O player redireciona para o preview público
      // (tratado em GamePlayerView); o master ignora.
      emit('sessionEnded', msg)
      return
    }
    if (type === 'error') {
      console.warn('[module_lab/ws] servidor reportou erro:', msg)
      emit('error', msg)
      return
    }
  }

  // inicia conexão
  connect()

  const adapter: Adapter = {
    sessionId,
    userId,
    role,

    on(event: string, cb: Listener): Unsubscribe {
      if (!listeners.has(event)) listeners.set(event, new Set())
      listeners.get(event)!.add(cb)
      // Replay do último snapshot pra listeners que chegaram tarde
      if (event === 'sessionSnapshot' && lastSnapshot) {
        try { cb(lastSnapshot) } catch (err) {
          console.error('[module_lab/ws] replay listener error', err)
        }
      }
      return () => {
        listeners.get(event)?.delete(cb)
      }
    },

    getCurrentSlideIndex: () => state.currentSlideIndex,
    getCurrentActivityId: () => state.currentActivityId,
    getInteractionMode: () => state.interactionMode,
    getQuizState: (questionId) => state.quizzes[questionId] ?? null,
    getScore: (membershipId): ScoreSnapshot => {
      const target = membershipId ?? state.membershipId
      if (!target) return { total: 0 }
      return { total: state.scores[target] ?? 0 }
    },
    getPairedResponse: (pairId) => state.pairedResponses[pairId] ?? null,
    getOwnResponse: (slideIndex) => state.ownResponsesBySlide[String(slideIndex)] ?? null,
    getRevealedResponses: (slideIndex) => state.revealedBySlide[String(slideIndex)] ?? [],
    getLabComponentState: (missionId, slideIndex) =>
      state.labComponentStates[`${missionId}:${slideIndex}`] ?? null,
    getMyAssignment: (slideIndex) =>
      state.myAssignmentBySlide[String(slideIndex)] ?? null,
    getPostits: (slideIndex) =>
      state.postitsBySlide[String(slideIndex)] ?? [],

    setSlide(index) {
      send({ type: 'setSlide', index })
    },
    setActivity(activityId) {
      send({ type: 'setActivity', activityId })
    },
    setInteractionMode(mode) {
      send({ type: 'setInteractionMode', mode })
    },
    openQuiz(payload) {
      send({ type: 'quizOpen', ...payload })
    },
    closeQuiz(questionId) {
      send({ type: 'quizClose', questionId })
    },
    resetQuiz(questionId) {
      send({ type: 'quizReset', questionId })
    },
    adjustScore(payload) {
      send({ type: 'adjustScore', ...payload })
    },
    submitAnswer(questionId, answerIndex) {
      send({ type: 'quizAnswer', questionId, answerIndex })
    },
    submitProgress({ slideId, progress }) {
      send({ type: 'progress', slideId, progress })
    },
    logEvent(event: TelemetryEvent) {
      send({ type: 'event', event })
    },
    submitOpenQuestion({ pairId, slideIndex, value }) {
      send({ type: 'openQuestionSubmit', pairId, slideIndex, value })
    },
    submitConfidence({ pairId, slideIndex, value }) {
      send({ type: 'confidenceSubmit', pairId, slideIndex, value })
    },
    submitQuizJustified({ questionId, slideIndex, optionId, justification, isCorrect }) {
      send({
        type: 'quizJustifiedAnswer',
        questionId, slideIndex, optionId, justification, isCorrect,
      })
    },
    submitForm({ slideIndex, values }) {
      send({ type: 'formSubmit', slideIndex, values })
    },
    revealOpenQuestion({ slideIndex, pairId, membershipId, revealed }) {
      send({ type: 'openQuestionReveal', slideIndex, pairId, membershipId, revealed })
    },
    createPostit({ slideIndex, text, columnId, anonymous }) {
      send({
        type: 'postitCreate',
        slideIndex,
        text,
        columnId: columnId ?? null,
        anonymous: anonymous ?? false,
      })
    },
    updatePostit({ postitId, text }) {
      send({ type: 'postitUpdate', postitId, text })
    },
    movePostit({ postitId, toColumn }) {
      send({ type: 'postitMove', postitId, toColumn })
    },
    deletePostit({ postitId }) {
      send({ type: 'postitDelete', postitId })
    },
    reactPostit({ postitId }) {
      send({ type: 'postitReact', postitId })
    },

    disconnect() {
      intentionallyClosed = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close()
      }
      listeners.clear()
    },
  }

  return adapter
}
